// @flow
import React, { useEffect, useState } from 'react';
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithPopup,
  signOut,
} from 'firebase/auth';
import {
  collection,
  deleteDoc,
  getFirestore,
  onSnapshot,
  query,
  updateDoc,
} from 'firebase/firestore';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import classNames from 'classnames';
import axios from 'axios';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

const app = initializeApp(firebaseConfig);

initializeAppCheck(app, {
  provider: new ReCaptchaV3Provider(process.env.REACT_APP_RECAPTCHA_SITE_KEY),
  isTokenAutoRefreshEnabled: true,
});

const auth: Object = getAuth(app);

const firestore: Object = getFirestore(app);

const BrutaskLogo = ({ className }: { className?: string }) => (
  <svg
    className={classNames('w-8', className)}
    viewBox="0 0 206 261"
    xmlns="http://www.w3.org/2000/svg">
    <g fill="none" fillRule="evenodd">
      <path
        fill="#FFF"
        d="M9.695 96.713 163 96c.547 22.741.82 43.745.82 63.01s-3.128 46.62-9.386 82.062H14.906c-4.49-11.786-6.715-20.98-6.673-27.584.138-21.391.206-38.432.206-51.123 0-18.568.419-40.452 1.256-65.652Z"
      />
      <path
        fill="#00BA63"
        d="M145.21 127.362c.027-1.72.016-3.44.014-5.16-.001-1.656-.336-3.25-1.07-4.732-.674-1.362-1.67-2.469-3.303-2.456-1.598.011-2.648.993-3.342 2.399-.779 1.576-1.127 3.233-1.158 4.987-.051 2.806-.038 5.622-.268 8.416-.284 3.427-.557 6.905-2.256 10.007-1.29 2.355-2.91 2.517-4.767.596-1.295-1.339-1.986-3.01-2.53-4.758-1.917-6.172-2.13-12.437-1.336-18.825.492-3.957-.746-6.31-3.01-6.776-2.356-.485-4.465 1.513-5.46 5.335-2.14 8.222-1.769 16.34 1.203 24.315 1.127 3.026 2.83 5.669 5.32 7.767 5.433 4.574 12.06 3.955 16.504-1.562 2.82-3.5 4.111-7.613 4.748-12.09.52-2.39.672-4.922.71-7.463ZM87.012 225.65c2.98 3.774 6.903 5.964 11.872 5.24 4.94-.719 8.136-3.782 9.938-8.32.807-2.035 1.131-4.195 1.045-6.408-.306-7.886-3.537-14.524-8.583-20.429-4.034-4.719-7.687-9.677-9.168-15.885-.61-2.56-.61-5.059.802-7.367.965-1.577 2.113-3.083 4.263-2.836 2.248.258 3.513 1.755 4.172 3.8.914 2.836.757 5.741.357 8.63-.254 1.84-.248 3.62.567 5.326 1.485 3.11 4.622 3.493 6.716.754.648-.848 1.28-1.856 1.48-2.877 1.144-5.807 1.243-11.6-1.545-17.019-4.08-7.926-13.978-9.535-20.183-3.3-3.116 3.129-4.844 6.942-4.935 11.454-.115 5.631 1.703 10.692 4.323 15.55 2.389 4.427 5.81 8.113 8.687 12.19 2.517 3.563 4.106 7.447 4.116 11.895.008 3.415-1.2 6.202-3.094 6.299-2.961.149-4.192-2.163-5.308-4.367-1.588-3.138-1.834-6.616-2.35-10.022-.36-2.374-.698-5.164-3.757-5.13-2.877.032-3.2 2.742-3.743 5.857-.503 5.575.192 11.726 4.328 16.965ZM40.9 207.9c-.3 2.698-.58 5.432.176 8.107.471 1.667 1.398 2.997 3.327 3.117 1.953.123 2.994-1.166 3.646-2.734.447-1.076.624-2.27.868-3.422.714-3.374 1.458-6.743 2.073-10.135.252-1.39.809-1.977 2.286-2.043 3.53-.159 7.059-.44 10.573-.81 1.788-.19 2.339.347 2.384 2.186.233 9.425.618 18.846.939 28.269.09 2.67.7 5.23 1.84 7.618.633 1.322 1.664 2.479 3.35 2.377 1.608-.097 2.602-1.08 3.212-2.554.881-2.128 1.429-4.374 1.366-6.625-.49-17.4-.715-34.81-1.895-52.181-.258-3.802-.874-7.585-2.592-11.068-3.45-6.997-11.54-8.248-16.913-2.626-1.58 1.654-2.832 3.54-3.838 5.586-4.801 9.763-7.606 20.165-9.742 30.768-.362 2.055-.83 4.099-1.06 6.17Zm56.95-78.292c8.829-3.411 13.894-9.956 15.36-19.189 1.307-8.224-4.262-14.175-12.59-13.846-3.165.124-6.047 1.108-8.781 2.617-2.378 1.312-4.372 3.134-6.584 5.43-.012-1.019-.01-1.519-.026-2.017-.095-2.895.057-5.812-.792-8.637-.48-1.596-1.337-2.796-3.171-2.888-1.712-.085-2.593.96-3.28 2.37-.854 1.753-1.083 3.643-1.389 5.518-1.82 11.169-1.622 22.408-1.15 33.654.19 4.529.351 9.066 1.63 13.461.46 1.582 1.288 2.9 3.145 2.88 1.667-.02 2.63-1.112 3.07-2.63.475-1.652 1.025-3.31 1.238-5.004.29-2.315.963-2.269 2.576-.896 6.082 5.179 12.512 9.916 19.013 14.55 2.95 2.104 6.046 4.022 9.486 5.253 1.56.557 3.05.357 4.172-1.065 1.154-1.462.804-2.939-.114-4.28-.762-1.114-1.702-2.12-2.653-3.086-4.926-5.007-10.755-8.892-16.323-13.103-2.937-2.22-5.85-4.472-9.157-7.001 2.543-.835 4.465-1.375 6.32-2.09Zm-67.983-13.15c-.37 3.61-.627 7.264.427 10.812 1.017 3.427 3.577 4.01 6.198 1.597.85-.782.865-1.754 1.029-2.713 1.892-11.182 4.333-22.235 8.06-32.963 1.153-3.322 2.493-6.619 5.006-9.222 3.261-3.377 7.628-2.545 9.33 1.817 1.121 2.87 1.098 5.746.134 8.756-1.958 6.12-6.085 10.071-11.641 12.966-3.362 1.753-3.356 4.25-.289 6.525 1.747 1.296 3.658 2.387 5.318 3.781 4.444 3.732 6.923 8.414 6.046 14.363-.556 3.785-1.826 7.385-5.685 9.02-3.603 1.528-6.71.042-9.503-2.32-1.887-1.596-3.989-2.827-6.365-3.575-1.603-.505-3.072-.376-4.257.893-1.142 1.22-1.02 2.685-.447 4.085 4.38 10.694 19.022 16.943 29.594 7.888 10.474-8.97 10.345-25.96-.321-35.378-1.799-1.589-2.014-2.244-.124-3.98 8.59-7.885 10.575-20.624 4.87-29.73-5.451-8.697-15.358-9.84-22.653-2.614a27.759 27.759 0 0 0-4.33 5.578c-4.988 8.4-7.564 17.628-9.153 27.17-.426 2.414-.994 4.813-1.244 7.244Zm133.431-49.427c1.698 10.172 1.492 20.444 1.406 30.691-.154 18.149-.533 36.296-.802 54.445-.018 1.23-.213 2.198-1.368 3.012a764.43 764.43 0 0 0-34.475 25.763c-.202.16-.464.244-1.016.524.07-2.483.137-4.77.202-7.057.018-.635.056-1.27.042-1.905-.071-3.375-1.894-6-4.241-6.126-2.242-.12-4.303 2.06-4.65 5.48a699.336 699.336 0 0 0-2.88 40.188c-.083 1.903-.16 3.809-.133 5.712.042 3.057 1.277 4.799 3.482 5.085 2.26.294 4.042-1.272 4.733-4.302.996-4.355 1.159-8.815 1.568-13.241.258-2.799.458-5.603.697-8.556 1.464.457 1.98 1.836 2.986 2.635 5.56 4.418 11.701 7.528 18.907 7.983 1.938.122 4.23.207 5.015-2.238.737-2.29-.734-3.734-2.426-4.97-2.792-2.04-6.055-3.201-9.056-4.854-2.781-1.532-5.44-3.21-7.866-5.261-.87-.737-1.41-1.289-.107-2.237 9.976-7.258 19.911-14.57 30.228-22.134.474 4.334.216 8.258.086 12.163-.45 13.607-.056 27.244-1.1 40.823-.654 8.488-1.396 16.98-3.957 25.232-2.24 7.219-7.267 10.585-14.27 11.56-8.73 1.215-17.558 1.35-26.338 1.342-19.497-.016-38.987.313-58.48.44-12.215.08-24.512-.273-36.513-3.195-8.435-2.053-12.78-6.898-14.057-15.571-1.062-7.203-.827-14.475-1.088-21.72-.522-14.51.554-29.007.324-43.512-.033-2.085.646-2.624 2.598-2.55 3.534.132 7.087.23 10.607-.012 2.219-.15 2.767.53 2.733 2.635-.177 11.248-.253 22.498-.322 33.748-.017 2.629.056 5.263.223 7.886.217 3.401 1.67 5.19 4.022 5.192 2.233.004 3.942-1.943 4.207-5.191.42-5.154.808-10.317.96-15.484.262-8.888.417-17.78.421-26.672 0-1.994.718-2.258 2.358-2.184 1.532.069 3.094.015 4.61-.213 2.964-.45 4.877-2.388 4.832-4.675-.045-2.302-1.959-4.322-4.924-4.587-3.334-.297-6.706-.293-10.058-.233-6.44.113-12.88.296-19.312.585-1.975.088-2.859-.115-2.847-2.53.076-16.329-.043-32.653.319-48.985.186-8.402-.798-16.841-.55-25.284.12-4.09.425-8.167 1.117-12.193 1.786-10.377 9.96-18.193 21.194-20.318 8.702-1.646 17.528-1.293 26.314-1.532 13.693-.372 27.391-1.11 41.079-.948 13.602.161 27.198.912 40.813.847 3.902-.019 7.734 1.007 11.535 1.925-5.726 6.156-11.451 12.314-17.178 18.47-2.774 2.981-5.608 5.91-8.303 8.961-1.63 1.845-1.879 3.988-.104 5.883 1.698 1.813 3.744 1.501 5.713.354 2.208-1.287 4.017-3.082 5.894-4.784 8.05-7.302 15.235-15.456 22.86-23.178 2.641 2.37 3.77 5.487 4.336 8.868Z"
      />
      <path
        fill="#DB648B"
        d="M192.728 18.195c.17-1.873-.786-3.046-2.48-3.341-1.772-.307-3.065.676-3.292 2.455-.223 1.745.7 2.978 2.502 3.287 1.872.322 2.897-.632 3.27-2.401m-28.545 6.008c.01 1.12-.222 2.745.217 4.354.513 1.877 1.468 3.414 3.565 3.596 2.139.186 3.387-1.213 3.98-3.047 1.41-4.368 2.951-8.483 7.678-10.538 1.43-.622 2.285-2.388 1.62-4.211-.693-1.898-2.347-2.423-4.146-2.429-.975-.003-1.985.236-2.922.54-5.504 1.788-8.654 5.69-9.992 11.735m-2.17 14.725c-2.954-4.003-5.114-8.238-5.152-13.434-.077-10.713 8.948-23.34 23.574-22.768 12.42.486 22.177 11.017 21.132 23.078-1.449 16.727-17.718 25.428-29.556 20.495-.743-.31-1.572-.41-2.362-.605l-7.636-6.766"
      />
      <path
        fill="#0D0E0D"
        d="m162.013 38.928 7.636 6.766-10.688 12.47c-7.624 7.722-14.81 15.876-22.86 23.178-1.876 1.702-3.686 3.496-5.894 4.784-1.968 1.147-4.014 1.459-5.712-.354-1.775-1.895-1.525-4.038.104-5.883 2.696-3.051 5.53-5.98 8.303-8.961l17.177-18.47c4.183-4.329 8.083-8.908 11.934-13.53"
      />
      <path
        fill="#FAFCFA"
        d="M31.11 109.215c1.59-9.542 4.166-18.77 9.155-27.17a27.784 27.784 0 0 1 4.33-5.578c7.293-7.226 17.2-6.083 22.651 2.614 5.707 9.105 3.72 21.845-4.87 29.731-1.89 1.734-1.674 2.39.125 3.978 10.666 9.419 10.796 26.408.32 35.38-10.571 9.053-25.213 2.806-29.592-7.89-.574-1.399-.696-2.863.445-4.085 1.187-1.268 2.654-1.396 4.258-.892 2.376.748 4.477 1.979 6.366 3.575 2.792 2.361 5.898 3.847 9.501 2.32 3.86-1.635 5.13-5.235 5.687-9.02.875-5.95-1.602-10.631-6.047-14.363-1.66-1.394-3.572-2.484-5.318-3.78-3.068-2.276-3.073-4.773.29-6.525 5.555-2.896 9.682-6.846 11.64-12.966.963-3.01.987-5.887-.134-8.758-1.702-4.361-6.069-5.193-9.33-1.816-2.513 2.603-3.852 5.9-5.006 9.221-3.727 10.728-6.168 21.781-8.06 32.964-.163.959-.18 1.93-1.03 2.713-2.62 2.414-5.18 1.83-6.197-1.597-1.053-3.548-.798-7.202-.426-10.812.249-2.431.817-4.83 1.242-7.244M86.274 119.261c-.718 3.836-.622 3.936 2.414 3.18 2.56-.636 5.055-1.463 7.4-2.693 3.545-1.863 5.947-4.731 7.141-8.527.5-1.587 1.397-3.46-.204-4.842-1.568-1.352-3.554-1.147-5.431-.496-.766.266-1.515.612-2.226 1.003-5.23 2.877-7.749 7.667-9.094 12.375m5.256 12.44c3.306 2.529 6.22 4.78 9.157 7 5.568 4.21 11.397 8.097 16.324 13.102.95.966 1.89 1.973 2.652 3.087.918 1.34 1.268 2.819.113 4.28-1.122 1.422-2.611 1.622-4.17 1.064-3.44-1.23-6.537-3.149-9.488-5.252-6.5-4.634-12.93-9.372-19.012-14.55-1.612-1.373-2.286-1.42-2.576.897-.212 1.694-.763 3.351-1.239 5.004-.438 1.518-1.402 2.609-3.07 2.629-1.856.02-2.684-1.298-3.145-2.88-1.278-4.395-1.44-8.932-1.63-13.46-.471-11.248-.668-22.487 1.15-33.654.307-1.876.536-3.766 1.39-5.518.687-1.41 1.568-2.456 3.28-2.37 1.833.09 2.69 1.29 3.17 2.887.85 2.824.697 5.742.793 8.636.017.5.014 1 .026 2.017 2.213-2.296 4.206-4.116 6.584-5.43 2.735-1.507 5.615-2.492 8.78-2.617 8.329-.328 13.897 5.622 12.591 13.847-1.466 9.233-6.531 15.778-15.361 19.188-1.854.717-3.776 1.257-6.319 2.092M53.813 191.184c3.753-.424 7.224-.868 10.707-1.183 1.209-.11 1.27-.829 1.227-1.673-.244-4.704-.247-9.435-1.276-14.062-.242-1.092-.235-2.661-1.628-2.842-1.187-.153-1.702 1.215-2.233 2.123-3.18 5.438-4.832 11.457-6.797 17.637M41.961 201.73c2.135-10.603 4.94-21.004 9.74-30.767 1.007-2.047 2.259-3.933 3.839-5.586 5.374-5.621 13.464-4.371 16.913 2.625 1.718 3.483 2.334 7.267 2.593 11.068 1.18 17.372 1.404 34.783 1.893 52.18.064 2.253-.483 4.498-1.364 6.628-.61 1.474-1.604 2.456-3.213 2.552-1.687.102-2.716-1.054-3.349-2.376-1.141-2.389-1.75-4.948-1.842-7.618-.32-9.422-.705-18.844-.938-28.27-.045-1.838-.596-2.374-2.384-2.185a175.84 175.84 0 0 1-10.572.81c-1.479.066-2.035.654-2.287 2.043-.616 3.392-1.36 6.76-2.074 10.135-.244 1.152-.42 2.347-.867 3.421-.652 1.569-1.692 2.859-3.646 2.736-1.93-.121-2.856-1.45-3.328-3.118-.755-2.675-.476-5.41-.175-8.108.23-2.07.699-4.114 1.061-6.17"
      />
      <path
        fill="#F9FBF9"
        d="M82.683 208.684c.545-3.115.868-5.825 3.743-5.857 3.06-.034 3.397 2.755 3.757 5.131.517 3.404.763 6.883 2.351 10.02 1.116 2.206 2.346 4.517 5.307 4.367 1.895-.096 3.103-2.884 3.095-6.299-.01-4.446-1.6-8.33-4.116-11.895-2.877-4.076-6.299-7.761-8.687-12.19-2.62-4.857-4.438-9.918-4.324-15.55.092-4.512 1.82-8.324 4.936-11.454 6.204-6.234 16.103-4.625 20.183 3.302 2.788 5.417 2.69 11.21 1.545 17.018-.201 1.021-.832 2.03-1.48 2.877-2.094 2.738-5.231 2.356-6.716-.753-.815-1.708-.821-3.486-.567-5.327.4-2.89.558-5.794-.357-8.63-.66-2.045-1.923-3.542-4.172-3.8-2.15-.246-3.298 1.26-4.263 2.836-1.413 2.309-1.411 4.807-.802 7.368 1.48 6.208 5.134 11.165 9.168 15.885 5.046 5.904 8.277 12.542 8.583 20.429.086 2.212-.238 4.372-1.046 6.406-1.8 4.539-4.997 7.602-9.937 8.321-4.97.723-8.89-1.466-11.87-5.24-4.138-5.24-4.833-11.39-4.33-16.965"
      />
      <path
        fill="#FAFCFA"
        d="M144.5 134.825c-.638 4.477-1.93 8.59-4.75 12.09-4.443 5.517-11.07 6.136-16.503 1.562-2.49-2.098-4.193-4.74-5.32-7.767-2.972-7.976-3.343-16.093-1.202-24.315.994-3.822 3.103-5.82 5.459-5.335 2.264.466 3.502 2.819 3.01 6.776-.793 6.388-.581 12.653 1.336 18.825.544 1.749 1.235 3.42 2.53 4.758 1.856 1.92 3.477 1.759 4.767-.596 1.699-3.102 1.972-6.58 2.256-10.007.23-2.794.217-5.61.268-8.416.031-1.754.38-3.411 1.158-4.987.694-1.406 1.744-2.388 3.342-2.4 1.633-.012 2.629 1.095 3.303 2.457.734 1.482 1.069 3.076 1.07 4.732.002 1.72.013 3.44-.014 5.16-.039 2.541-.19 5.073-.71 7.463M164.183 24.203c1.338-6.045 4.488-9.947 9.992-11.735.937-.304 1.947-.543 2.923-.54 1.798.005 3.452.531 4.145 2.428.665 1.825-.19 3.59-1.62 4.212-4.727 2.055-6.268 6.17-7.679 10.538-.592 1.834-1.84 3.232-3.98 3.047-2.095-.182-3.051-1.718-3.564-3.596-.44-1.608-.207-3.235-.217-4.354"
      />
      <path
        fill="#F9FBF9"
        d="M192.728 18.195c-.373 1.769-1.398 2.723-3.27 2.401-1.803-.31-2.725-1.542-2.502-3.287.227-1.779 1.52-2.762 3.291-2.455 1.695.295 2.652 1.468 2.48 3.34"
      />
      <path
        fill="#56B86C"
        d="M86.274 119.261c1.345-4.708 3.865-9.498 9.094-12.375.711-.39 1.46-.737 2.226-1.003 1.877-.651 3.863-.856 5.43.496 1.602 1.381.704 3.255.205 4.842-1.194 3.796-3.596 6.664-7.142 8.527-2.344 1.23-4.84 2.057-7.399 2.693-3.036.756-3.132.656-2.414-3.18M53.813 191.184c1.965-6.18 3.618-12.199 6.797-17.637.531-.908 1.046-2.276 2.233-2.123 1.393.18 1.386 1.75 1.628 2.842 1.03 4.627 1.032 9.358 1.276 14.062.043.844-.018 1.563-1.227 1.673-3.483.315-6.954.759-10.707 1.183"
      />
    </g>
  </svg>
);

const GoogleLogo = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="self-start w-5 mr-3"
    viewBox="0 0 18 18">
    <defs></defs>
    <path
      fill="#EA4335"
      d="M9 3.48c1.69 0 2.83.73 3.48 1.34l2.54-2.48C13.46.89 11.43 0 9 0 5.48 0 2.44 2.02.96 4.96l2.91 2.26C4.6 5.05 6.62 3.48 9 3.48z"></path>
    <path
      fill="#4285F4"
      d="M17.64 9.2c0-.74-.06-1.28-.19-1.84H9v3.34h4.96c-.1.83-.64 2.08-1.84 2.92l2.84 2.2c1.7-1.57 2.68-3.88 2.68-6.62z"></path>
    <path
      fill="#FBBC05"
      d="M3.88 10.78A5.54 5.54 0 013.58 9c0-.62.11-1.22.29-1.78L.96 4.96A9.008 9.008 0 000 9c0 1.45.35 2.82.96 4.04l2.92-2.26z"></path>
    <path
      fill="#34A853"
      d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.84-2.2c-.76.53-1.78.9-3.12.9-2.38 0-4.4-1.57-5.12-3.74L.97 13.04C2.45 15.98 5.48 18 9 18z"></path>
    <path fill="none" d="M0 0h18v18H0z"></path>
  </svg>
);

const columnHeaderClassName = classNames(
  'px-6 py-4 font-bold text-gray-900 text-left',
);

const columnClassName = classNames(
  'px-6 py-4 text-xs font-medium text-gray-900 whitespace-nowrap ',
);

const App = (): React$Node => {
  const [approving, setApproving] = useState('');

  const [authenticated, setAuthenticated] = useState(false);

  const [loading, setLoading] = useState(true);

  const [session, setSession] = useState({});

  const [users, setUsers] = useState([]);

  const onGoogleSignIn = async () => {
    try {
      const provider = new GoogleAuthProvider();

      const response = await signInWithPopup(auth, provider);

      const { email } = response.user.toJSON();

      const domain = email.split('@')?.[1];

      setAuthenticated(domain === 'brucira.com');
    } catch (e) {}
  };

  const onSignOut = () =>
    signOut(auth).then(() => {
      setAuthenticated(false);
    });

  const onApprove = (user) => async () => {
    try {
      setApproving(user.id);

      const { accessToken: token } = await auth.currentUser;

      await axios.post(
        `https://us-central1-bru-task.cloudfunctions.net/api/v2/waitlist/approve/${user.id}`,
        {},
        {
          headers: {
            Authorization: token,
          },
        },
      );

      await user.update({ isApproved: true });

      alert('User is approved and invitation sent');
    } catch (e) {
      alert('Something went wrong, inform Debashis 🤪');
    } finally {
      setApproving('');
    }
  };

  useEffect(() => {
    onAuthStateChanged(auth, (user) => {
      if (user) {
        const { displayName, email, uid } = user.toJSON();

        const domain = email.split('@')?.[1];

        setAuthenticated(domain === 'brucira.com');

        setSession({ displayName, email, uid });
      }
    });
  }, []);

  useEffect(() => {
    if (authenticated) {
      onSnapshot(query(collection(firestore, 'waitlist')), (snapshot) => {
        setUsers(
          snapshot.docs
            .map((d) => ({
              ...d.data(),
              id: d.id,
              remove: () =>
                window.confirm('Are you sure you want to delete?')
                  ? deleteDoc(d.ref)
                  : {},
              update: (payload) => updateDoc(d.ref, payload),
            }))
            .sort((a, b) => a.position - b.position),
        );

        setLoading(false);
      });
    }
  }, [authenticated]);

  if (!authenticated) {
    return (
      <div className="flex flex-col items-center justify-center gap-8 min-h-screen w-screen bg-slate-100">
        <div className="flex flex-col justify-center items-center gap-4">
          <BrutaskLogo className="!w-16" />
          <h1 className="text-3xl font-semibold">Brutask Admin</h1>
        </div>
        <button
          className={classNames(
            'flex justify-center',
            'my-2 py-3 px-5',
            'bg-white rounded-full',
            'shadow hover:shadow-md focus:outline-none focus:ring focus:ring-slate-200 appearance-none',
            'transition-shadow duration-300',
          )}
          onClick={onGoogleSignIn}
          type="button">
          <GoogleLogo />
          <span>Continue with Google</span>
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-screen bg-slate-100">
      <div className="flex flex-col container min-h-screen mx-auto">
        <div className="flex flex-col justify-center items-center gap-2 p-4">
          <BrutaskLogo />
          <h1 className="text-xl font-semibold">Brutask Waiting List</h1>
        </div>
        <div className="grow p-4">
          <table
            className={classNames(
              'w-full text-sm',
              'bg-white shadow rounded overflow-hidden',
              'border-collapse table-auto',
            )}>
            <thead className="border-b-2">
              <tr>
                <th scope="col" className={columnHeaderClassName}>
                  #
                </th>
                <th scope="col" className={columnHeaderClassName}>
                  Serial No.<sup>1</sup>
                </th>
                <th scope="col" className={columnHeaderClassName}>
                  Name
                </th>
                <th scope="col" className={columnHeaderClassName}>
                  Email
                </th>
                <th scope="col" className={columnHeaderClassName}>
                  Status
                </th>
                <th scope="col" className={columnHeaderClassName}>
                  Related Tweet<sup>2</sup>
                </th>
                <th scope="col" className={columnHeaderClassName}>
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr className="border-b">
                  <td className={columnClassName} colSpan={6}>
                    Loading...
                  </td>
                </tr>
              ) : (
                users.map((user, index) => (
                  <tr className="border-b last:border-b-0" key={user.id}>
                    <td className={columnClassName}>{index + 1}</td>
                    <td className={columnClassName}>{user.position}</td>
                    <td className={columnClassName}>{user.displayName}</td>
                    <td className={columnClassName}>
                      <a
                        className="text-green-500 underline"
                        href={`mailto:${user.email}`}>
                        {user.email}
                      </a>
                    </td>
                    <td className={classNames(columnClassName, 'uppercase')}>
                      {user.isApproved ? 'Approved' : 'Waiting'}
                    </td>
                    <td className={columnClassName}>
                      <a
                        className="text-green-500 underline"
                        href={`https://twitter.com/search?lang=en&q=${encodeURIComponent(
                          `(#${user.position}) (@brutaskapp)`,
                        )}&src=typed_query`}
                        rel="noreferrer"
                        target="_blank">
                        View
                      </a>
                    </td>
                    <td
                      className={classNames(
                        columnClassName,
                        'flex justify-start items-center gap-2',
                      )}>
                      <button
                        className={classNames(
                          'py-0.5 px-1.5',
                          'text-xs font-medium text-white appearance-none',
                          'rounded',
                          user.isApproved ? 'bg-blue-500' : 'bg-green-500',
                        )}
                        onClick={onApprove(user)}
                        type="button">
                        {user.isApproved ? 'Resend' : 'Approve'}
                      </button>
                      <button
                        className={classNames(
                          'py-0.5 px-1.5',
                          'text-xs font-medium text-white appearance-none',
                          'bg-red-500 rounded',
                          'disabled:opacity-60 disabled:cursor-not-allowed',
                        )}
                        disabled={user.isApproved || approving === user.id}
                        onClick={user.remove}
                        type="button">
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <div className="flex flex-col items-center py-8 px-4">
            <p className="text-xs">
              <sup>1</sup> Position in waiting list starts from 1843
            </p>
            <p className="text-xs">
              <sup>2</sup> Optional, available if tweeted by the user
            </p>
          </div>
        </div>
        <div className="flex justify-center items-center p-4">
          <button
            className="text-xs font-semibold appearance-none"
            onClick={onSignOut}
            type="button">
            {session?.email} <span className="text-red-500">(Sign Out)</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default App;
